/**
 * ServiceVisualsBoard — the on-page mini-board that picks 6 of the 15
 * `ServiceVisuals` and arranges them into a 6-up grid for the given
 * service slug. Each tile carries a short bilingual eyebrow + title so
 * the visual + copy form a complete unit.
 *
 * Owner directive: "10–15 SVG/CSS animated visualizations themed for
 * JTR/JEC (data streams, pulse rings, ETC gate flows, etc.) mounted
 * across the three detail pages."
 *
 * Mounted by `pages/ServiceDetailPage.jsx` between the testimonial
 * spotlight and the "Who it's for" section.
 *
 * Service-specific picks:
 *   • japanetccard       — ETC card lifecycle + customer trust signals
 *   • japantollreceipts  — Receipts pipeline + accuracy + bilingual reports
 *   • japanchangemoney   — Forex liveness + multilingual + KYC trust
 */

import {
  ServiceVisualStyles,
  EtcGateFlow,
  TollMeisaiPulse,
  PlateScanCascade,
  NetworkPing,
  FleetGridHeartbeat,
  ReceiptStream,
  DataPipeline,
  OrbitingNodes,
  RadarSweep,
  LedgerWaveform,
  ShieldPulse,
  HumanInTheLoopRing,
  MultilingualOrbit,
  AccuracyGauge,
  CountUpBadge,
} from "./ServiceVisuals";

const BOARDS = {
  japanetccard: [
    { Viz: EtcGateFlow,        eyebrow: { en: "Lane flow",        ja: "ゲート通過" },        title: { en: "Every car. Every gate. Logged.",       ja: "1台ずつ、1ゲートずつ、確実に記録。" },        h: "h-32" },
    { Viz: HumanInTheLoopRing, eyebrow: { en: "Human approval",   ja: "人による最終承認" },    title: { en: "AI drafts. A human signs off.",        ja: "AIが下書き。最終承認は必ず人。" },       h: "h-32" },
    { Viz: PlateScanCascade,   eyebrow: { en: "Plate match",      ja: "ナンバー突合" },       title: { en: "Plate → vehicle → driver, automatically.", ja: "ナンバー → 車両 → ドライバーを自動で突合。" }, h: "h-40" },
    { Viz: NetworkPing,        eyebrow: { en: "Nationwide",       ja: "全国対応" },           title: { en: "Customers across Japan, day and night.", ja: "日本全国のお客様を、昼夜を問わず。" },    h: "h-40" },
    { Viz: ShieldPulse,        eyebrow: { en: "Verified",         ja: "本人確認済み" },       title: { en: "KYC-backed, every issuance.",          ja: "発行時の本人確認・適合性チェックを必ず実施。" }, h: "h-32" },
    { Viz: CountUpBadge,       eyebrow: { en: "Always-on",        ja: "24/7体制" },          title: { en: "Support active in EN + JA.",           ja: "日英バイリンガル、24/7サポート稼働。" },   h: "h-32" },
  ],
  japantollreceipts: [
    { Viz: ReceiptStream,      eyebrow: { en: "Receipt flow",     ja: "領収書フロー" },       title: { en: "Receipts in your inbox every morning.", ja: "毎朝、メールに領収書が届きます。" },     h: "h-32" },
    { Viz: DataPipeline,       eyebrow: { en: "AI pipeline",      ja: "AIパイプライン" },     title: { en: "Input → Extract → Validate → Export.", ja: "入力 → 抽出 → 検証 → 出力。" },         h: "h-32" },
    { Viz: TollMeisaiPulse,    eyebrow: { en: "MEISAI sync",      ja: "MEISAI同期" },        title: { en: "Hourly checks, never a missed trip.",   ja: "毎時の取り込みで、走行データを取りこぼしません。" }, h: "h-40" },
    { Viz: AccuracyGauge,      eyebrow: { en: "Validated",        ja: "検証済み" },          title: { en: "99.97% delivery accuracy.",            ja: "配信精度 99.97%。" },                     h: "h-40" },
    { Viz: MultilingualOrbit,  eyebrow: { en: "Bilingual",        ja: "日英対応" },          title: { en: "EN + JA columns, side-by-side.",       ja: "日英2列、横並びで出力。" },               h: "h-32" },
    { Viz: LedgerWaveform,     eyebrow: { en: "Audit-ready",      ja: "監査対応" },          title: { en: "Every record, ready for review.",       ja: "1件1件のレコードを、いつでも監査可能に。" },  h: "h-32" },
  ],
  japanchangemoney: [
    { Viz: MultilingualOrbit,  eyebrow: { en: "Bilingual",        ja: "日英対応" },          title: { en: "Built EN + JA from day one.",           ja: "1日目から、日英両対応で設計。" },         h: "h-32" },
    { Viz: ShieldPulse,        eyebrow: { en: "KYC-aware",        ja: "本人確認に配慮" },     title: { en: "Identity-verification aware design.",   ja: "本人確認・反マネーロンダリングに配慮。" }, h: "h-32" },
    { Viz: NetworkPing,        eyebrow: { en: "Nationwide",       ja: "全国対応予定" },       title: { en: "Mainland Japan + Okinawa coverage.",   ja: "本土・沖縄をカバー予定。" },                 h: "h-40" },
    { Viz: AccuracyGauge,      eyebrow: { en: "Live rates",       ja: "リアルタイム" },       title: { en: "Transparent, real-time rates.",         ja: "透明性のあるリアルタイム為替レート。" },   h: "h-40", vizProps: { value: 99.9 } },
    { Viz: OrbitingNodes,      eyebrow: { en: "Same AI team",     ja: "同じAIチーム" },       title: { en: "Operated by the same 5-member team.",   ja: "同じ5つのAI支援ワークフローが運営します。" },         h: "h-32" },
    { Viz: RadarSweep,         eyebrow: { en: "Always-on",        ja: "常時稼働" },          title: { en: "Markets monitored around the clock.",   ja: "為替市場を24時間モニタリング。" },         h: "h-32" },
  ],
};

export default function ServiceVisualsBoard({ slug, accent = "#0F1D2D", isJa = false }) {
  const tiles = BOARDS[slug];
  if (!tiles || tiles.length === 0) return null;

  return (
    <section
      data-testid={`service-detail-${slug}-visuals-board`}
      className="bg-white border-b border-[#E2E8F0]"
    >
      {/* shared keyframes live in a single style block — included once
            per page; cheap because it's a single component instance. */}
      <ServiceVisualStyles />

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="max-w-3xl mb-10 md:mb-12">
          <div
            data-testid={`service-detail-${slug}-visuals-eyebrow`}
            className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold mb-3"
            style={{ color: accent }}
          >
            {isJa ? "私たちの運用ディシプリン" : "Our operating discipline"}
          </div>
          <h2
            className={[
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tighter leading-[1.08]",
            ].join(" ")}
            data-testid={`service-detail-${slug}-visuals-title`}
          >
            {isJa
              ? "言葉ではなく、動きで証明します。"
              : "What the words actually look like when running."}
          </h2>
          <p className="mt-4 text-[15px] leading-[1.85] text-[#475569] max-w-2xl">
            {isJa
              ? "上で挙げたお約束を、実際にお客様体験として何が動いているのかを、ひと目で確認できるかたちにしました。"
              : "The promises above, shown as the live mechanics that actually move every day behind the scenes."}
          </p>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {tiles.map((t, i) => {
            const Viz = t.Viz;
            const vizProps = { accent, ...(t.vizProps || {}) };
            return (
              <li
                key={`${slug}-viz-${i}`}
                data-testid={`service-detail-${slug}-visual-${i + 1}`}
                className="group rounded-2xl ring-1 ring-[#E2E8F0] bg-white overflow-hidden transition-all hover:-translate-y-0.5 hover:ring-transparent hover:shadow-[0_25px_55px_-30px_rgba(15,29,45,0.35)]"
              >
                <div
                  className={`${t.h} flex items-center justify-center`}
                  style={{ background: `${accent}0D` /* ~5% accent tint */ }}
                >
                  <Viz {...vizProps} />
                </div>
                <div className="p-5">
                  <div
                    className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold mb-2"
                    style={{ color: accent }}
                  >
                    {isJa ? t.eyebrow.ja : t.eyebrow.en}
                  </div>
                  <p
                    className={[
                      isJa ? "font-ja" : "font-display",
                      "text-[#0F1D2D] text-[15px] md:text-[16px] font-bold tracking-tight leading-snug",
                    ].join(" ")}
                  >
                    {isJa ? t.title.ja : t.title.en}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
